/**
 * AIConsole Component - AI-powered chat interface
 *
 * Features:
 * - Interactive AI conversations with Claude/OpenAI
 * - Project-based context and memory
 * - Multiple context types (general, project, technical, estimation)
 * - Conversation history management
 * - Available across all user roles (Admin, Developer, PM, Client)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Button,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import aiConsoleService, {
  AIConversation,
  AIMessage,
  CreateConversationRequest,
  ConversationWithMessages,
  ConversationStats
} from '../../services/AIConsoleService';
import ConversationList from './ConversationList';
import MessageArea from './MessageArea';
import MessageCompose from './MessageCompose';
import NewConversationDialog from './NewConversationDialog';
import { logger } from '../../utils/logger';

const StyledPaper = styled(Paper)(({ theme }) => ({
  height: '100%',
  minHeight: '600px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative'
}));

interface AIConsoleProps {
  projectId?: number;
  defaultContextType?: 'general' | 'project' | 'technical' | 'estimation';
  height?: string;
}

export const AIConsole: React.FC<AIConsoleProps> = ({
  projectId,
  defaultContextType = 'general',
  height = '100%'
}) => {
  const [conversations, setConversations] = useState<AIConversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<ConversationWithMessages | null>(null);
  const [conversationStats, setConversationStats] = useState<ConversationStats | null>(null);
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [newConversationDialogOpen, setNewConversationDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Load conversations on mount and when projectId changes
  useEffect(() => {
    loadConversations();
  }, [projectId]);

  const loadConversations = async () => {
    setLoading(true);
    setError(null);

    try {
      logger.info('Loading AI conversations', { projectId });
      const data = await aiConsoleService.listConversations(projectId);
      setConversations(data);
      logger.info('AI conversations loaded successfully', { count: data.length });
    } catch (err: any) {
      logger.error('Failed to load AI conversations', { error: err });
      setError('Failed to load conversations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectConversation = useCallback(async (conversation: AIConversation) => {
    setMessagesLoading(true);
    setError(null);

    try {
      logger.info('Loading AI conversation', { conversationId: conversation.id });
      const { conversation: fullConversation, stats } = await aiConsoleService.getConversation(conversation.id);

      setSelectedConversation(fullConversation);
      setMessages(fullConversation.messages || []);
      setConversationStats(stats);

      logger.info('AI conversation loaded successfully', {
        conversationId: conversation.id,
        messageCount: fullConversation.messages?.length || 0
      });
    } catch (err: any) {
      logger.error('Failed to load AI conversation', { error: err, conversationId: conversation.id });
      setError('Failed to load conversation. Please try again.');
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  const handleCreateConversation = async (request: CreateConversationRequest) => {
    try {
      logger.info('Creating AI conversation', { title: request.title });
      const newConversation = await aiConsoleService.createConversation(request);

      // Add to conversations list
      setConversations(prev => [newConversation, ...prev]);

      // Auto-select the new conversation
      setSelectedConversation(newConversation);
      setMessages(newConversation.messages || []);

      setSuccessMessage('Conversation created successfully');
      setNewConversationDialogOpen(false);

      logger.info('AI conversation created successfully', { conversationId: newConversation.id });
    } catch (err: any) {
      logger.error('Failed to create AI conversation', { error: err });
      setError('Failed to create conversation. Please try again.');
      throw err;
    }
  };

  const handleSendMessage = async (content: string) => {
    if (!selectedConversation || !content.trim()) return;

    setSendingMessage(true);
    setError(null);

    try {
      logger.info('Sending message to AI', {
        conversationId: selectedConversation.id,
        messageLength: content.length
      });

      const result = await aiConsoleService.sendMessage(selectedConversation.id, content);

      // Add both user and assistant messages to the list
      setMessages(prev => [...prev, result.user_message, result.assistant_message]);

      // Update conversation in the list (move to top and update last_message_at)
      setConversations(prev => {
        const updated = prev.map(conv =>
          conv.id === selectedConversation.id
            ? {
                ...conv,
                last_message_at: result.assistant_message.created_at,
                message_count: (conv.message_count || 0) + 2
              }
            : conv
        );
        // Sort by last_message_at to move updated conversation to top
        return updated.sort((a, b) =>
          new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime()
        );
      });

      logger.info('Message sent successfully', {
        conversationId: selectedConversation.id,
        processingTime: result.processing_time_ms
      });
    } catch (err: any) {
      logger.error('Failed to send message to AI', { error: err, conversationId: selectedConversation.id });
      setError('Failed to send message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleArchiveConversation = async (conversationId: number) => {
    try {
      logger.info('Archiving AI conversation', { conversationId });
      await aiConsoleService.archiveConversation(conversationId);

      // Remove from conversations list
      setConversations(prev => prev.filter(conv => conv.id !== conversationId));

      // Clear selection if archived conversation was selected
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
        setMessages([]);
      }

      setSuccessMessage('Conversation archived successfully');
      logger.info('AI conversation archived successfully', { conversationId });
    } catch (err: any) {
      logger.error('Failed to archive AI conversation', { error: err, conversationId });
      setError('Failed to archive conversation. Please try again.');
    }
  };

  const handleDeleteConversation = async (conversationId: number) => {
    if (!window.confirm('Are you sure you want to delete this conversation? This action cannot be undone.')) {
      return;
    }

    try {
      logger.info('Deleting AI conversation', { conversationId });
      await aiConsoleService.deleteConversation(conversationId);

      // Remove from conversations list
      setConversations(prev => prev.filter(conv => conv.id !== conversationId));

      // Clear selection if deleted conversation was selected
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
        setMessages([]);
      }

      setSuccessMessage('Conversation deleted successfully');
      logger.info('AI conversation deleted successfully', { conversationId });
    } catch (err: any) {
      logger.error('Failed to delete AI conversation', { error: err, conversationId });
      setError('Failed to delete conversation. Please try again.');
    }
  };

  const handleRetry = () => {
    setError(null);
    loadConversations();
  };

  const filteredConversations = conversations.filter(conv =>
    conv.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box data-testid="ai-console" sx={{ width: '100%', height }}>
      <StyledPaper elevation={3}>
        {error && (
          <Alert
            severity="error"
            data-testid="error-message"
            sx={{ m: 1 }}
            onClose={() => setError(null)}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={handleRetry}
                data-testid="retry-button"
                startIcon={<RefreshIcon />}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {loading && conversations.length === 0 && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ p: 3 }}
            data-testid="conversations-loading"
          >
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading conversations...
            </Typography>
          </Box>
        )}

        <Box display="flex" sx={{ height: '100%', overflow: 'hidden' }}>
          <Box sx={{ width: '33.33%', borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
            <ConversationList
              conversations={filteredConversations}
              selectedConversationId={selectedConversation?.id}
              onSelect={handleSelectConversation}
              onArchive={handleArchiveConversation}
              onDelete={handleDeleteConversation}
              onNewConversation={() => setNewConversationDialogOpen(true)}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              loading={loading}
            />
          </Box>

          <Box sx={{ width: '66.67%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {selectedConversation ? (
              <>
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  <MessageArea
                    conversation={selectedConversation}
                    messages={messages}
                    stats={conversationStats}
                    loading={messagesLoading}
                  />
                </Box>
                <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
                  <MessageCompose
                    onSend={handleSendMessage}
                    disabled={sendingMessage}
                    placeholder="Ask me anything..."
                  />
                </Box>
              </>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                sx={{ height: '100%', p: 3 }}
              >
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Select a conversation or start a new one
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  AI Console helps you with project planning, technical questions, and estimations
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setNewConversationDialogOpen(true)}
                  data-testid="start-conversation-button"
                >
                  Start New Conversation
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </StyledPaper>

      <NewConversationDialog
        open={newConversationDialogOpen}
        onClose={() => setNewConversationDialogOpen(false)}
        onCreate={handleCreateConversation}
        defaultProjectId={projectId}
        defaultContextType={defaultContextType}
      />

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />
    </Box>
  );
};

export default AIConsole;
